import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService, corporateEmailValidator, PostHogService } from '@cosmos/angular/utils';
import { TranslatePipe } from '@ngx-translate/core';
import { MessageModule } from 'primeng/message';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { Password } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { AuthLayoutComponent } from '../auth-layout/auth-layout.component';

@Component({
  selector: 'cosmos-signup',
  imports: [
    TranslatePipe, ReactiveFormsModule, RouterModule,
    FloatLabelModule, InputTextModule, PasswordModule, Password,
    ButtonModule, CheckboxModule, MessageModule, InputGroupModule, InputGroupAddonModule,
    AuthLayoutComponent,
  ],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignupComponent {
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);

  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly postHogService = inject(PostHogService);
  private intentCaptured = false;

  readonly signupForm = this.fb.group({
    name:        ['', Validators.required],
    surname:     ['', Validators.required],
    email:       ['', [Validators.required, Validators.email, corporateEmailValidator]],
    password:    ['', [Validators.required, Validators.minLength(8)]],
    companyName: ['', Validators.required],
    jobTitle:    ['', Validators.required],
    phone:       ['', [Validators.required, Validators.pattern(/^\d{9}$/)]],
    terms:            [false, Validators.requiredTrue],
    marketingConsent: [false, Validators.requiredTrue],
  });

  onFormFieldFocus(): void {
    if (this.intentCaptured) return;
    this.intentCaptured = true;
    this.postHogService.capture('self-serve registration intent', {});
  }

  onSubmitForm(): void {
    if (this.signupForm.invalid) return;
    this.loading.set(true);
    this.error.set(null);
    const raw = this.signupForm.getRawValue();
    const payload = { ...raw, phone: `+34${raw.phone}` };
    this.authService.registerFree(payload as any).subscribe({
      next: (response: any) => {
        this.loading.set(false);
        // Resolves this browser's own anonymous posthog-js distinct-ID (the one that
        // fired the "registration intent" event) onto the real user — the backend's
        // own identify() call merges onto a separate server-side posthog-node
        // anonymous ID, so without this, the intent event never joins the funnel.
        const userId = response?.data?._id;
        if (userId) {
          this.postHogService.identify(userId, {
            email: response.data.email,
            name: response.data.name,
          });
        }
        // registerFree() already logged the user in (stateService.setCurrentUser),
        // so go straight to /validate instead of bouncing through /login.
        this.router.navigate(['/validate']);
      },
      error: (err: any) => {
        this.error.set(err?.error?.message ?? 'auth.signup.errorGeneric');
        this.loading.set(false);
      },
    });
  }
}
